import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';
import { AuthService } from '../services/auth.service';
import { BrandService } from '../../services/brand-config.service';
import {
  SupportedLanguage,
  TranslationService,
} from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { AppComponent } from '../app.component';
import { SessionService } from '../services/session.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatProgressSpinner, LanguageSwitcherComponent, TranslatePipe],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
  /** 'checking' = waiting for the recovery session from the email link,
   *  'ready' = session present, show the form,
   *  'invalid' = no recovery session (link expired/opened directly),
   *  'done' = password updated. */
  state: 'checking' | 'ready' | 'invalid' | 'done' = 'checking';
  busy = false;
  password = '';
  confirm = '';
  errorMsg = '';
  currentLanguage: SupportedLanguage = 'en';
  loginUrl: string;

  private email = '';

  constructor(
    public auth: AuthService,
    public brand: BrandService,
    public translationService: TranslationService,
    private snackBar: MatSnackBar,
    private router: Router,
    private appComponent: AppComponent,
    private session: SessionService,
  ) {
    this.loginUrl = `/${this.appComponent.clerkLocalizationLang || 'en'}/login`;
    this.init();
  }

  private async init() {
    this.currentLanguage = await this.translationService.getCurrentLanguageAsync();
    this.state = (await this.establishRecoverySession()) ? 'ready' : 'invalid';
  }

  // The recovery email can land here three ways depending on the GoTrue flow:
  //   • ?token_hash=…&type=recovery  — OTP/token-hash (works cross-device, no
  //     PKCE verifier needed). The most robust; preferred template format.
  //   • #access_token=…&refresh_token=…  — implicit-flow fragment.
  //   • ?code=…  — PKCE; supabase-js auto-exchanges it during init.
  // We establish the session explicitly (so a tab without the PKCE verifier, or
  // an implicit hash a PKCE-configured client otherwise ignores, still works)
  // and confirm it server-side with getUser() — a stale token left in storage
  // would otherwise show the form, then fail updateUser with "Auth session
  // missing!".
  private async establishRecoverySession(): Promise<boolean> {
    try {
      const url = new URL(window.location.href);
      const tokenHash = url.searchParams.get('token_hash');
      const type = url.searchParams.get('type');
      const code = url.searchParams.get('code');
      const hashParams = window.location.hash.startsWith('#')
        ? new URLSearchParams(window.location.hash.slice(1))
        : null;
      const accessToken = hashParams?.get('access_token');

      if (tokenHash) {
        const t = type === 'email' ? 'email' : 'recovery';
        const { error } = await this.auth.verifyOtp(tokenHash, t);
        if (error) return false;
      } else if (accessToken) {
        const { error } = await this.auth.setSession(
          accessToken,
          hashParams?.get('refresh_token') ?? '',
        );
        if (error) return false;
      } else if (code) {
        // PKCE: detectSessionInUrl exchanges the code during client init.
        await this.auth.waitUntilReady();
      }

      const { data, error } = await this.auth.getUser();
      if (error || !data?.user) return false;
      this.email = data.user.email ?? '';
      // Strip tokens from the address bar so a refresh/back doesn't replay them.
      history.replaceState(null, '', window.location.pathname);
      return true;
    } catch {
      return false;
    }
  }

  private t(key: string): string {
    return this.translationService.translate(key);
  }

  async submit() {
    if (this.busy) return;
    this.errorMsg = '';
    if (this.password.length < 6) {
      this.errorMsg = this.t('auth.errors.passwordMin');
      return;
    }
    if (this.password !== this.confirm) {
      this.errorMsg = this.t('auth.errors.passwordMismatch');
      return;
    }
    this.busy = true;
    try {
      const { error } = await this.auth.updatePassword(this.password);
      if (error) {
        this.errorMsg = error.message || this.t('auth.errors.resetFailed');
        return;
      }
      // Password set. Drop the recovery session and send the user to a clean
      // sign-in (prefilled), where the normal flow builds the app session.
      this.state = 'done';
      try {
        sessionStorage.removeItem('authIntent');
      } catch {}
      if (this.email) this.session.setAuthPrefill({ email: this.email });
      this.appComponent.clearPasswordReset();
      await this.auth.signOut();
      this.snackBar.open(this.t('auth.reset.updated'), 'OK', { duration: 5000 });
      this.router.navigate([this.loginUrl]);
    } finally {
      this.busy = false;
    }
  }
}
